import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, DollarSign, Users, Clock, Car, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { whyChooseUs } from '../mockData';

export const Home = () => {
  const navigate = useNavigate();

  const features = [
    { icon: Shield, title: 'Reliable Service', description: 'Professional airport transfers with punctual, dependable service.' },
    { icon: DollarSign, title: 'Flat Rates by City', description: 'Transparent pricing with no surprises. Know your cost upfront.' },
    { icon: Users, title: 'Professional Drivers', description: 'Licensed, insured, and courteous chauffeurs for your comfort.' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_86bfc4f8-b687-473a-9179-c22fa1fae018/artifacts/qmg6bn5d_image.png"
            alt="LiveryRUs luxury SUV at O'Hare Airport - Chicago limo service"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight">
            Luxury Chicago Airport Transportation
          </h1>
          <p className="text-xl sm:text-2xl text-gray-200 mb-8 font-light">
            Serving Chicagoland North & Northwest Suburbs Since 2004
          </p>

          {/* Bullet Points */}
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-8 mb-10">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-[#00D4FF] rounded-full" />
              <span className="text-white text-lg">Professional, insured drivers</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-[#00D4FF] rounded-full" />
              <span className="text-white text-lg">Sedans & SUVs available</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-[#00D4FF] rounded-full" />
              <span className="text-white text-lg">Flat-rate airport transfers</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Button
              onClick={() => navigate('/book')}
              className="bg-[#00D4FF] hover:bg-[#00B8E6] text-black font-bold px-8 py-6 text-lg transition-all duration-200 hover:scale-105 shadow-xl hover:shadow-[#00D4FF]/50"
            >
              Request Your Ride
            </Button>
            <Button
              onClick={() => navigate('/cities-rates')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-black font-semibold px-8 py-6 text-lg transition-all duration-200 hover:scale-105"
            >
              View Cities & Rates
            </Button>
          </div>
        </div>

      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Why Choose LiveryRUs
            </h2>
            <div className="w-24 h-1 bg-[#00D4FF] mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={index}
                  className="group hover:shadow-2xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-2"
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 bg-black rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#00D4FF] transition-colors duration-300">
                      <Icon className="w-10 h-10 text-[#00D4FF] group-hover:text-black transition-colors duration-300" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-[#00D4FF] mb-2">20+</div>
              <div className="text-gray-400 uppercase tracking-wide text-sm">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#00D4FF] mb-2">40+</div>
              <div className="text-gray-400 uppercase tracking-wide text-sm">Cities Served</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#00D4FF] mb-2">100%</div>
              <div className="text-gray-400 uppercase tracking-wide text-sm">Professional</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-[#00D4FF] mb-2">100%</div>
              <div className="text-gray-400 uppercase tracking-wide text-sm">Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Ready for Your Next Journey?
          </h2>
          <p className="text-xl text-gray-600 mb-10">
            Book your airport transfer today and experience the LiveryRUs difference.
          </p>
          <Button
            onClick={() => navigate('/book')}
            className="bg-[#00D4FF] hover:bg-[#00B8E6] text-black font-bold px-12 py-6 text-lg transition-all duration-200 hover:scale-105 shadow-xl hover:shadow-[#00D4FF]/50"
          >
            Request Your Ride Now
          </Button>
        </div>
      </section>
    </div>
  );
};